import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens, Loader } from '@hotjar/hotsauce';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSite } from '@hotjar/site';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { useRecentSurveys } from '@hotjar/webapp/hooks/useRecentSurveys';
import { Card } from '@hotjar/webapp/components/Card';
import { WidgetGhostButton } from '../WidgetGhostButton';
import { RecentSurveyItem } from './RecentSurveyItem';
import { EMPTY_STATE_TOOL, EMPTY_STATE_KIND, NewEmptyWidget } from '../NewEmptyWidget';
import { getForYouExperimentWidgetHeaderConfig } from '../../utils';

const StyledLoader = styled(Loader)`
  padding: ${tokens.spacer.x4};
`;

const Table = styled.table`
  width: 100%;
`;

const CardBody = styled(Card.Body)`
  padding: 0;
`;

const ListFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${tokens.spacer.x1pt5};
  border-top: 1px solid ${tokens.color.ui_border_light};
`;

export function SurveysCard({ inForYouExperiment }: { inForYouExperiment?: boolean }): JSX.Element {
  const { t } = useTranslation();
  const { data: currentSite, isLoading: isCurrentSiteLoading } = useCurrentSite();
  const { click } = useTrack();
  const trackClick = (elementName: string) => click(elementName, 'Space Overview Surveys Card', {});
  const { data: surveys, isLoading, isError } = useRecentSurveys();
  const enabledSurveys = surveys?.filter(({ enabled }) => enabled);
  const isEmpty = isError || !enabledSurveys || enabledSurveys.length === 0;

  return (
    <Card>
      {isLoading || isCurrentSiteLoading ? (
        <StyledLoader />
      ) : (
        <>
          <Card.Header
            icon={getForYouExperimentWidgetHeaderConfig('surveys', inForYouExperiment).icon}
            variant={getForYouExperimentWidgetHeaderConfig('surveys', inForYouExperiment).variant}
            href={
              currentSite ? getRoute(SURVEYS.RESPONSES.path, { siteId: currentSite.id }) : undefined
            }
            onClick={() => trackClick('Card Title')}
            title={t('activation:dashboard.surveys-card.recent-surveys')}
          />
          {isEmpty ? (
            <NewEmptyWidget tool={EMPTY_STATE_TOOL.surveys} kind={EMPTY_STATE_KIND.EMPTY} />
          ) : (
            <CardBody>
              <Table>
                <tbody>
                  {enabledSurveys?.map((survey) => (
                    <RecentSurveyItem key={survey.id} {...survey} />
                  ))}
                </tbody>
              </Table>
              <ListFooter>
                <WidgetGhostButton
                  as="a"
                  href={
                    currentSite
                      ? getRoute(SURVEYS.RESPONSES.path, { siteId: currentSite.id })
                      : undefined
                  }
                  onClick={() => trackClick('Card Footer Button')}
                >
                  {t('activation:dashboard.surveys-card.footer')}
                </WidgetGhostButton>
              </ListFooter>
            </CardBody>
          )}
        </>
      )}
    </Card>
  );
}
